import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TRADITION_GROUPS,
  TRADITION_LABELS,
  TRADITION_SUBCATEGORIES,
  type Tradition,
  type TraditionGroup,
} from '../theme/tokens';

const TRADITION_EMOJI: Record<Tradition, string> = {
  christian: '✝️',
  catholic: '⛪',
  protestant: '📖',
  orthodox_christian: '☦️',
  islamic: '☪️',
  sunni: '☪️',
  shia: '☪️',
  hindu: '🕉️',
  vaishnavism: '🪷',
  shaivism: '🔱',
  shaktism: '🌺',
  buddhist: '☸️',
  theravada: '🌿',
  mahayana: '🪷',
  vajrayana: '💎',
  chinese_folk: '🏮',
  taoist: '☯️',
  religious_taoism: '🏮',
  philosophical_taoism: '☯️',
  jewish: '✡️',
  orthodox_judaism: '✡️',
  conservative_judaism: '✡️',
  reform_judaism: '✡️',
  crystal_healing: '💎',
  sound_bath: '🔔',
  aromatherapy: '🌸',
  chromotherapy: '🌈',
  somatic_mindfulness: '🎯',
  earthing: '🪨',
  breathwork: '🌬️',
  qi_gong: '🥋',
  floatation: '🌊',
  stoic: '🏛️',
};

const GROUPS: { id: TraditionGroup; slug: string; labelKey: string }[] = [
  { id: 'Major Religions', slug: 'major', labelKey: 'traditionsPicker.groups.major' },
  { id: 'Alternative Practices', slug: 'alternative', labelKey: 'traditionsPicker.groups.alternative' },
];

interface TraditionPickerProps {
  selected: Tradition[];
  onChange: (traditions: Tradition[]) => void;
}

/** Multi-select traditions. Review direction ("make denominational detail expandable"): schools
 * and denominations sit behind a per-tradition disclosure, open by default only when one of them
 * is already selected. */
export function TraditionPicker({ selected, onChange }: TraditionPickerProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Partial<Record<Tradition, boolean>>>(() =>
    Object.fromEntries(
      Object.entries(TRADITION_SUBCATEGORIES).map(([parent, subs]) => [parent, (subs ?? []).some((s) => selected.includes(s))]),
    ),
  );

  const toggle = (tr: Tradition) => {
    onChange(selected.includes(tr) ? selected.filter((x) => x !== tr) : [...selected, tr]);
  };

  const chip = (tr: Tradition, sub = false) => (
    <button
      type="button"
      className={`tradition-chip${sub ? ' tradition-subchip' : ''}${selected.includes(tr) ? ' selected' : ''}`}
      onClick={() => toggle(tr)}
      aria-pressed={selected.includes(tr)}
    >
      <span className="emoji" aria-hidden="true">{TRADITION_EMOJI[tr]}</span>
      <span>{TRADITION_LABELS[tr]}</span>
    </button>
  );

  return (
    <div className="tradition-groups">
      {GROUPS.map((group) => (
        <div key={group.id} className="tradition-group" role="group" aria-labelledby={`tradition-group-${group.slug}`}>
          <h3 id={`tradition-group-${group.slug}`} className="tradition-group-title">{t(group.labelKey)}</h3>
          <div className="tradition-grid">
            {TRADITION_GROUPS[group.id].map((tr) => {
              const subs = TRADITION_SUBCATEGORIES[tr];
              if (!subs) {
                return <div key={tr} className="tradition-item">{chip(tr)}</div>;
              }
              const isOpen = Boolean(expanded[tr]);
              const panelId = `tradition-subs-${tr}`;
              const selectedSubs = subs.filter((s) => selected.includes(s)).length;
              return (
                <div key={tr} className="tradition-item has-subs">
                  <div className="tradition-parent-row">
                    {chip(tr)}
                    <button
                      type="button"
                      className="tradition-subtoggle"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setExpanded((prev) => ({ ...prev, [tr]: !isOpen }))}
                    >
                      {isOpen ? t('traditionsPicker.hideSchools') : t('traditionsPicker.showSchools', { count: subs.length })}
                      {!isOpen && selectedSubs > 0 && (
                        <span className="tradition-subtoggle-count">{t('traditionsPicker.selectedCount', { count: selectedSubs })}</span>
                      )}
                    </button>
                  </div>
                  <div
                    id={panelId}
                    className="tradition-subgrid"
                    role="group"
                    aria-label={t('traditionsPicker.schoolsLabel', { tradition: TRADITION_LABELS[tr] })}
                    hidden={!isOpen}
                  >
                    {subs.map((s) => <span key={s}>{chip(s, true)}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
